export type Coordinate = [number, number];
interface Feature {
  properties: {
    kind: string;
    osm_id: string;
    name?: string;
    highway?: string;
    building?: string;
  };
  geometry: { type: string; coordinates: Coordinate[] | Coordinate[][] };
}
interface Geography {
  features: Feature[];
  extracted: string;
}
export const geography: Geography = { features: [], extracted: "2026-10-10" };
let pending: Promise<void> | null = null;
export const emptyGeometry = {
  roofs: [],
  sides: [],
  parks: "",
  water: "",
  rails: "",
  streets: "",
  arteries: "",
  paths: "",
  cathedral: "",
  waterLabels: [],
};
export function loadGeography(): Promise<void> {
  if (geography.features.length) return Promise.resolve();
  if (!pending)
    pending = fetch(`${import.meta.env.BASE_URL}geography/lichfield.geojson`)
      .then((r) => {
        if (!r.ok) throw new Error("Geography unavailable");
        return r.json();
      })
      .then((data: Geography) => {
        if (!Array.isArray(data.features) || !data.features.length)
          throw new Error("Invalid geography");
        geography.features = data.features;
        cache.clear();
      })
      .catch((e) => {
        pending = null;
        throw e;
      });
  return pending;
}
export const project = (c: Coordinate, tilted = true): Coordinate => {
  const e = (c[0] + 1.826) * 33750,
    n = (52.685 - c[1]) * 55650;
  return tilted
    ? [850 + e * 0.94 - n * 0.34, 570 + e * 0.27 + n * 0.67]
    : [850 + e, 570 + n];
};
const path = (cs: Coordinate[], tilt: boolean, height = 0) =>
  cs
    .map((c, i) => {
      const p = project(c, tilt);
      return `${i ? "L" : "M"}${p[0].toFixed(1)},${(p[1] - height).toFixed(1)}`;
    })
    .join("");
export function getMapGeometry(tilt: boolean) {
  const roofs = Array.from({ length: 5 }, () => ""),
    sides = Array.from({ length: 3 }, () => "");
  let parks = "",
    water = "",
    rails = "",
    streets = "",
    arteries = "",
    paths = "",
    cathedral = "";
  let waterLabels: { name: string; point: Coordinate }[] = [];
  for (const f of geography.features) {
    const isPolygon = f.geometry.type === "Polygon";
    const cs = (
      isPolygon ? f.geometry.coordinates[0] : f.geometry.coordinates
    ) as Coordinate[];
    const k = f.properties.kind;
    if (k === "building") {
      const id = Number(f.properties.osm_id),
        h = tilt
          ? f.properties.building === "cathedral"
            ? 23
            : 4 + (id % 5) * 1.4
          : 0;
      const roof = path(cs, tilt, h) + "Z";
      roofs[id % 5] += roof;
      if (f.properties.building === "cathedral") cathedral = roof;
      if (tilt) {
        // Side faces are illustrative extrusion, not measured building heights.
        for (let i = 0; i < cs.length - 1; i++) {
          const a = project(cs[i], tilt),
            b = project(cs[i + 1], tilt);
          if (b[0] - a[0] < 0) continue;
          sides[i % 3] +=
            `M${a[0].toFixed(1)},${a[1].toFixed(1)}L${b[0].toFixed(1)},${b[1].toFixed(1)}L${b[0].toFixed(1)},${(b[1] - h).toFixed(1)}L${a[0].toFixed(1)},${(a[1] - h).toFixed(1)}Z`;
        }
      }
    } else if (k === "park") parks += path(cs, tilt) + "Z";
    else if (k === "water") {
      water += path(cs, tilt) + "Z";
      if (f.properties.name?.includes("Pool")) {
        const avg: Coordinate = [
          cs.reduce((a, c) => a + c[0], 0) / cs.length,
          cs.reduce((a, c) => a + c[1], 0) / cs.length,
        ];
        waterLabels.push({
          name: f.properties.name,
          point: project(avg, tilt),
        });
      }
    } else if (k === "rail") rails += path(cs, tilt);
    else if (
      ["primary", "secondary", "tertiary"].includes(f.properties.highway || "")
    )
      arteries += path(cs, tilt);
    else if (
      ["footway", "path", "cycleway", "pedestrian"].includes(
        f.properties.highway || "",
      )
    )
      paths += path(cs, tilt);
    else streets += path(cs, tilt);
  }
  return {
    roofs,
    sides,
    parks,
    water,
    rails,
    streets,
    arteries,
    paths,
    cathedral,
    waterLabels,
  };
}
// Cache both projections. Camera motion changes the viewBox, never the geometry.
const cache = new Map<boolean, ReturnType<typeof getMapGeometry>>();
export function mapGeometry(tilt: boolean) {
  if (!geography.features.length) return emptyGeometry;
  if (!cache.has(tilt)) cache.set(tilt, getMapGeometry(tilt));
  return cache.get(tilt)!;
}
